// ---------------------------------------------------------------------------
// routeShape — route-shape coherence gates.
//
// Pure geometry, no network — so these are fully unit-testable and form the
// SAFETY FLOOR for route generation: a candidate that fails any of these can
// never be surfaced as a card, regardless of how pretty it scores.
//
// The rule (Morgan's route philosophy): a great route can be LONG — a
// cross-country scenic road trip is a perfectly valid Scenic option — but it
// must be COHERENT. It always heads toward where you're going; it never
// doubles back, spurs out to a point and returns, or loops through the same
// area. Length is fine. Weird shapes are not.
//
// The core signal is "monotonic progress": as you travel the polyline, your
// distance to the destination should keep shrinking. A spur out to a lookout
// and back, or a loop, shows up as the route moving AWAY from the destination
// after it had gotten closer — that's a "backtrack", and we measure it.
// ---------------------------------------------------------------------------

import { haversine, type LngLat } from "@/utils/polyline";

/**
 * The farthest (in meters) the route ever moves AWAY from the destination
 * after having gotten closer to it. A straight run → ~0. A 5 km spur out to a
 * viewpoint and back → ~5 km. A loop → large. This is the heart of the
 * monotonic-progress gate.
 */
// ---------------------------------------------------------------------------
// MEMOISED PURE GEOMETRY (Aug 27 2026 — the physical-iPhone freeze).
//
// Same reasoning as `hasNearReturn` in scoring.ts, and the same measurement:
// the coherence gate evaluates these predicates over and over on the SAME
// coordinate array — twice inside one `passes()`, once per gate built (the
// quarantine pass plus classifyRoutes' direct / pleasant / scenic), and four
// to five times per search as generateRoutes re-classifies. After
// `hasNearReturn` was cached, a CPU profile of one 15-route × 3,000-point
// `classifyThreeCards` put `hasTightKnot` and the `haversine` calls under
// `maxBacktrackMeters` at the top of the remaining self-time.
//
// Both are pure functions of the coordinate array, and nothing in the app
// mutates a route's `coordinates` after it is built. So the answers are
// cached by array identity. Caching is DELIBERATELY declined when the caller
// passes anything that could change the answer — non-default knot options, or
// a `dest` that is not the route's own last point — so the cache can only ever
// return the value the function would have computed anyway.
// ---------------------------------------------------------------------------
const SHAPE_MEMO = {
  backtrack: new WeakMap<LngLat[], number>(),
  tightKnot: new WeakMap<LngLat[], boolean>(),
  retrace: new WeakMap<LngLat[], number>(),
};

export function maxBacktrackMeters(coords: LngLat[], dest: LngLat): number {
  // Only the route-to-its-own-endpoint case is cacheable; every other `dest`
  // computes fresh.
  const cacheable = coords.length > 0 && dest === coords[coords.length - 1];
  if (cacheable) {
    const hit = SHAPE_MEMO.backtrack.get(coords);
    if (hit !== undefined) return hit;
  }
  const value = computeMaxBacktrackMeters(coords, dest);
  if (cacheable) SHAPE_MEMO.backtrack.set(coords, value);
  return value;
}

function computeMaxBacktrackMeters(coords: LngLat[], dest: LngLat): number {
  if (coords.length < 2) return 0;
  let closestSoFar = Infinity;
  let maxBack = 0;
  for (const c of coords) {
    const d = haversine(c, dest);
    if (d < closestSoFar) {
      closestSoFar = d;
    } else {
      const back = d - closestSoFar;
      if (back > maxBack) maxBack = back;
    }
  }
  return maxBack;
}

/**
 * `maxBacktrackMeters` expressed as a fraction of the straight-line trip
 * distance, so a single threshold works for a 2-mile errand and a 2,000-mile
 * road trip alike. 0 = perfectly monotonic; 0.5 = wandered half the trip's
 * distance backward at some point.
 */
export function backtrackFraction(coords: LngLat[], start: LngLat, dest: LngLat): number {
  const direct = haversine(start, dest);
  if (direct < 1) return 0; // start ≈ dest; nothing meaningful to measure
  return maxBacktrackMeters(coords, dest) / direct;
}

/**
 * Project a point onto the straight A→B axis using a local equirectangular
 * approximation (good for the corridor scales we care about). Returns:
 *   - `t`: position along the axis — 0 at A, 1 at B. <0 is "behind" the start,
 *     >1 is "beyond" the destination.
 *   - `perpM`: perpendicular distance from the axis, in meters.
 *
 * Used by corridor discovery to keep only scenic anchors that lie ON THE WAY
 * (0 < t < 1, modest perpM) and to order entry/exit anchors along the trip —
 * so we route ALONG a corridor between A and B rather than spurring out to it.
 */
export function axisProjection(p: LngLat, a: LngLat, b: LngLat): { t: number; perpM: number } {
  const latRef = (a[1] * Math.PI) / 180;
  const mPerDegLng = 111_320 * Math.cos(latRef);
  const mPerDegLat = 110_540;
  const toXY = (q: LngLat): [number, number] => [
    (q[0] - a[0]) * mPerDegLng,
    (q[1] - a[1]) * mPerDegLat,
  ];
  const B = toXY(b);
  const P = toXY(p);
  const len2 = B[0] * B[0] + B[1] * B[1];
  if (len2 <= 0) return { t: 0, perpM: Math.hypot(P[0], P[1]) };
  const t = (P[0] * B[0] + P[1] * B[1]) / len2;
  const projX = B[0] * t;
  const projY = B[1] * t;
  const perpM = Math.hypot(P[0] - projX, P[1] - projY);
  return { t, perpM };
}

/** Keep one point roughly every `stepM` meters — cheap decimation so the
 *  O(n·window) knot scan below stays fast on dense polylines. */
function decimate(coords: LngLat[], stepM: number): LngLat[] {
  if (coords.length <= 2) return coords;
  const out: LngLat[] = [coords[0]];
  let acc = 0;
  for (let i = 1; i < coords.length; i++) {
    acc += haversine(coords[i - 1], coords[i]);
    if (acc >= stepM) { out.push(coords[i]); acc = 0; }
  }
  const last = coords[coords.length - 1];
  if (out[out.length - 1] !== last) out.push(last);
  return out;
}

export interface KnotOpts {
  /** Two points this close in SPACE (meters)... */
  nearM?: number;
  /** ...but at least this far apart along the PATH (meters) = a knot. */
  minPathM?: number;
  /** Don't bother scanning pairs farther apart than this along the path —
   *  a real knot/jog returns within a few km. Bounds the scan. */
  maxWindowM?: number;
}

/**
 * Detects a tight knot / spur / jog: two points on the route that are close in
 * SPACE but far apart along the PATH — i.e. the route left a spot and came
 * right back to it. This is the artifact of jogging out to touch an exact
 * waypoint that's slightly off the road. Distinct from the GLOBAL backtrack
 * gate, which a long trip's overall progress would hide this inside.
 */
export function hasTightKnot(coords: LngLat[], opts: KnotOpts = {}): boolean {
  // Default options only — a caller that tunes a threshold gets a fresh scan.
  const cacheable = opts.nearM === undefined
    && opts.minPathM === undefined
    && opts.maxWindowM === undefined;
  if (cacheable) {
    const hit = SHAPE_MEMO.tightKnot.get(coords);
    if (hit !== undefined) return hit;
  }
  const value = computeTightKnot(coords, opts);
  if (cacheable) SHAPE_MEMO.tightKnot.set(coords, value);
  return value;
}

function computeTightKnot(coords: LngLat[], opts: KnotOpts): boolean {
  const nearM = opts.nearM ?? 80;
  const pts = decimate(coords, 25);
  if (pts.length < 4) return false;
  const cum: number[] = [0];
  for (let i = 1; i < pts.length; i++) cum[i] = cum[i - 1] + haversine(pts[i - 1], pts[i]);
  // SCALE-AWARE WINDOW (Morgan, Aug 8 — the fifth gate of the family found on
  // San Jose → Flagstaff, and the one still killing Route 66 after the other
  // four were fixed):
  //     [pool] coherence rejects by gate — tightKnot:3 nearReturn:1
  //
  // A knot is two points within 80 m that sit 500 m–4 km apart along the road.
  // That is a lasso on a town drive. It is also a HAIRPIN — and Historic
  // Route 66 climbs the Oatman switchbacks, which are nothing but hairpins.
  // Held at a fixed window, this gate rejects the great drives it is supposed
  // to protect, and gets more certain to do it the longer the road.
  //
  // A knot worth refusing is one the driver would SEE as wasted travel. So
  // both bounds scale: the wasted path must be at least 0.5% of the trip, and
  // the window widens to 2% so genuinely large lassos are still caught. Trips
  // under ~100 km keep the original 500 m / 4 km exactly.
  const tripM = cum[cum.length - 1];
  const minPathM = opts.minPathM ?? Math.max(500, tripM * 0.005);
  const maxWindowM = opts.maxWindowM ?? Math.max(4000, tripM * 0.02);
  for (let i = 0; i < pts.length; i++) {
    for (let j = i + 1; j < pts.length; j++) {
      const path = cum[j] - cum[i];
      if (path < minPathM) continue;
      if (path > maxWindowM) break;
      if (haversine(pts[i], pts[j]) <= nearM) return true;
    }
  }
  return false;
}

/**
 * The longest stretch of road this route drives TWICE, in opposite directions.
 *
 * Morgan, Aug 30, from a real search: "this route had me go the opposite
 * direction from the place i intended down a road and back, retracing the road
 * the whole way until finally going the right direction on the same road. it's
 * just extra needless driving."
 *
 * WHY THIS AND NOT `hasTightKnot`. The knot gate asks "did the route come back
 * near a point it already visited". That is also true of a SWITCHBACK, and
 * this codebase has already been bitten by it — Historic Route 66 climbs the
 * Oatman hairpins and got rejected as a knot. So the knot gate was deliberately
 * blunted, and a retrace this size now slips under it.
 *
 * A retrace is a different shape from a hairpin, and the difference is
 * measurable:
 *
 *   - a hairpin's two legs are ANTIPARALLEL but SEPARATED — there is a
 *     hillside between them, and they diverge within a few tens of metres;
 *   - a retrace is the SAME pavement, driven back over itself, and it stays
 *     that way for the length of the road.
 *
 * So this asks for antiparallel travel within `nearM` that CONTINUES for a
 * meaningful distance. A hairpin scores a few tens of metres. Driving half a
 * mile up a road and back scores half a mile.
 *
 * Returns metres of doubled-back road — 0 for a route that never retraces.
 * Cheap: grid-bucketed, so it is linear in the number of points rather than
 * quadratic.
 */
export function retracedMeters(coords: LngLat[], opts: { nearM?: number } = {}): number {
  const cacheable = opts.nearM === undefined;
  if (cacheable) {
    const hit = SHAPE_MEMO.retrace.get(coords);
    if (hit !== undefined) return hit;
  }
  const value = computeRetracedMeters(coords, opts.nearM ?? 22);
  if (cacheable) SHAPE_MEMO.retrace.set(coords, value);
  return value;
}

/** Unit direction of the path at vertex i, or null at a degenerate vertex. */
function headingAt(coords: LngLat[], i: number): [number, number] | null {
  const a = coords[Math.max(0, i - 1)];
  const b = coords[Math.min(coords.length - 1, i + 1)];
  const latRef = (a[1] * Math.PI) / 180;
  const dx = (b[0] - a[0]) * Math.cos(latRef);
  const dy = b[1] - a[1];
  const m = Math.hypot(dx, dy);
  if (m < 1e-12) return null;
  return [dx / m, dy / m];
}

function computeRetracedMeters(coords: LngLat[], nearM: number): number {
  const n = coords.length;
  if (n < 4) return 0;

  const cum: number[] = [0];
  for (let i = 1; i < n; i++) cum[i] = cum[i - 1] + haversine(coords[i - 1], coords[i]);

  // Grid buckets of ~nearM so each vertex only compares against neighbours.
  const latRef = (coords[0][1] * Math.PI) / 180;
  const dLat = nearM / 111320;
  const dLng = nearM / (111320 * Math.max(0.2, Math.cos(latRef)));
  const cell = new Map<string, number[]>();
  const key = (p: LngLat) =>
    `${Math.floor(p[1] / dLat)},${Math.floor(p[0] / dLng)}`;
  for (let i = 0; i < n; i++) {
    const k = key(coords[i]);
    const arr = cell.get(k);
    if (arr) arr.push(i);
    else cell.set(k, [i]);
  }

  // A vertex is "retracing" when some FAR-ALONG-THE-PATH vertex sits within
  // nearM of it and is travelling the opposite way. 150 degrees of separation:
  // a U-turn is 180, an ordinary bend is well under.
  const ANTIPARALLEL = Math.cos((150 * Math.PI) / 180); // = -0.866
  // Ignore neighbours reached within this much path — that is just the road's
  // own width and the vertices either side of the current one.
  const MIN_PATH_M = 120;

  const retracing = new Uint8Array(n);
  for (let i = 0; i < n; i++) {
    const hi = headingAt(coords, i);
    if (!hi) continue;
    const cy = Math.floor(coords[i][1] / dLat);
    const cx = Math.floor(coords[i][0] / dLng);
    outer: for (let ox = -1; ox <= 1; ox++) {
      for (let oy = -1; oy <= 1; oy++) {
        const bucket = cell.get(`${cy + oy},${cx + ox}`);
        if (!bucket) continue;
        for (const j of bucket) {
          if (Math.abs(cum[j] - cum[i]) < MIN_PATH_M) continue;
          if (haversine(coords[i], coords[j]) > nearM) continue;
          const hj = headingAt(coords, j);
          if (!hj) continue;
          if (hi[0] * hj[0] + hi[1] * hj[1] <= ANTIPARALLEL) {
            retracing[i] = 1;
            break outer;
          }
        }
      }
    }
  }

  // Longest CONTINUOUS run. A hairpin marks a handful of vertices; a road
  // driven up and back marks the whole road.
  let best = 0;
  let runStart = -1;
  for (let i = 0; i < n; i++) {
    if (retracing[i]) {
      if (runStart < 0) runStart = i;
    } else if (runStart >= 0) {
      best = Math.max(best, cum[i - 1] - cum[runStart]);
      runStart = -1;
    }
  }
  if (runStart >= 0) best = Math.max(best, cum[n - 1] - cum[runStart]);
  return best;
}

/**
 * Trim little OUT-AND-BACK retrace spurs from a route polyline (Morgan: the
 * route goes down a block or half a block, turns right back the way it came, and
 * rejoins). These come from threading a waypoint that sits just off the through
 * line — the engine touches it and reverses. We KEEP the route (it runs the
 * great roads we asked for) and just snip the dead retrace: wherever the path
 * leaves a point and returns to within `nearM` of it after a SHORT bounded
 * excursion, drop the excursion and reconnect. Bounded to block-scale spurs so
 * a real loop or a long legitimate detour is never eaten. Returns a new array.
 */
export function trimOutAndBackSpurs(
  coords: LngLat[],
  opts: { nearM?: number; minSpurM?: number; maxSpurM?: number } = {},
): LngLat[] {
  const nearM = opts.nearM ?? 22;     // returns to within ~22m = the same spot
  const minSpurM = opts.minSpurM ?? 12; // ignore GPS-jitter wiggles
  // Raised 400 -> 1200: a scenic anchor placed a bit off the through-line makes
  // the engine jog out ~0.3-0.7 mi and back, which the old cap left in (Morgan:
  // "spurs for the scenic route"). Still safe to raise because a snip only
  // happens on a TRUE out-and-back retrace — the path must return within 22m of
  // where it left AND resume the same heading (the guard below); a real detour
  // or hairpin never does both.
  const maxSpurM = opts.maxSpurM ?? 1200;
  if (coords.length < 4) return coords;
  const cum: number[] = [0];
  for (let i = 1; i < coords.length; i++) cum[i] = cum[i - 1] + haversine(coords[i - 1], coords[i]);
  const bearing = (a: LngLat, b: LngLat): number =>
    Math.atan2(b[1] - a[1], (b[0] - a[0]) * Math.cos((a[1] * Math.PI) / 180));
  const angDiff = (x: number, y: number): number => {
    let d = Math.abs(x - y);
    if (d > Math.PI) d = 2 * Math.PI - d;
    return d;
  };
  const out: LngLat[] = [];
  let i = 0;
  while (i < coords.length) {
    out.push(coords[i]);
    // Farthest point that returns to near coords[i] within a short excursion.
    let jBack = -1;
    for (let j = i + 2; j < coords.length; j++) {
      const travel = cum[j] - cum[i];
      if (travel < minSpurM) continue;
      if (travel > maxSpurM) break;
      if (haversine(coords[i], coords[j]) <= nearM) jBack = j;
    }
    if (jBack >= 0) {
      // Only a true RETRACE if the route CONTINUES the same direction after
      // rejoining (you were going west, jogged out and back, keep going west).
      // A corner/turn brings the path near a recent point too, but the heading
      // changes — don't round those off.
      const before = bearing(coords[Math.max(0, i - 2)], coords[i]);
      const after = bearing(coords[jBack], coords[Math.min(coords.length - 1, jBack + 2)]);
      if (angDiff(before, after) < Math.PI / 4) {
        i = jBack + 1; // skip the out-and-back excursion
        continue;
      }
    }
    i++;
  }
  return out;
}

export interface CoherenceOpts {
  /**
   * Max allowed backtrack as a fraction of the straight-line distance. A route
   * that ever wanders more than this fraction of the trip's distance backward
   * is an out-and-back / loop and is rejected. Default 0.25 (25%).
   */
  maxBacktrackFraction?: number;
}

/**
 * True when the route makes coherent progress toward the destination — no
 * significant backtracking. Loops, spurs, and out-and-backs all register as
 * backtracks and fail this. Length is NOT considered: a long-but-coherent
 * road trip passes.
 */
export function makesCoherentProgress(
  coords: LngLat[],
  start: LngLat,
  dest: LngLat,
  opts: CoherenceOpts = {},
): boolean {
  const cap = opts.maxBacktrackFraction ?? 0.25;
  return backtrackFraction(coords, start, dest) <= cap;
}
